import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { cn } from '@/utils/cn';
import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';
import { shouldYieldEscape } from '@/hooks/useEscapeLayer';

/**
 * BottomSheet Component
 *
 * A mobile-friendly slide-up drawer built on Radix Dialog primitives.
 * Used across the app for popovers, previews, and action menus on touch devices.
 *
 * Uses forceMount + internal visibility state to enable smooth
 * enter/exit CSS transitions (Radix unmounts portal content by default,
 * which kills exit animations).
 */

export interface BottomSheetProps {
  /** Whether the bottom sheet is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Content to display */
  children: React.ReactNode;
  /** Optional trigger element (tap to open) — if not provided, control via open/onOpenChange */
  trigger?: React.ReactNode;
  /** Max height as CSS value (default: '85dvh'); the body scrolls beyond it */
  maxHeight?: string;
  /** Show close button in top-right corner (default: true) */
  showCloseButton?: boolean;
  /** Additional classes for the content container */
  className?: string;
  /** Whether to show the drag handle bar at top (default: true) */
  showDragHandle?: boolean;
  /** Animation style: 'slide' (slide up from bottom) or 'fade' (fade + scale in). Default: 'slide' */
  animation?: 'slide' | 'fade';
  /** Allow pointer events on trigger content (needed for drag-and-drop). Default: false */
  allowTriggerPointerEvents?: boolean;
  /** Extra classes for the focusable trigger wrapper; merged after the default focus treatment, so it can override it. */
  triggerClassName?: string;
  /** Corner radius of the trigger's keyboard-focus frame: 'xl' (default, tiles) or 'full' (pills, avatars). */
  triggerFrameRadius?: TriggerFrameRadius;
  /** Optional: override Radix focus restore on close (call e.preventDefault() and focus your own element). */
  onCloseAutoFocus?: (event: Event) => void;
  /** Optional: called once after the sheet has fully unmounted (post exit animation), whatever the close path. */
  onClosed?: () => void;
}

const ANIMATION_DURATION = 300;

/**
 * Focus treatment for the trigger wrapper, whatever the child is. No browser outline; the keyboard indicator is a
 * border-colour shift on an absolutely positioned overlay (transparent until focus-visible), so it never changes
 * layout and does not depend on the child having a border. Corner radius comes from the
 * triggerFrameRadius prop (one radius class only, so nothing depends on CSS order).
 */
const TRIGGER_FRAME_RADIUS = {
  xl: 'after:rounded-xl',
  full: 'after:rounded-full',
} as const;

export type TriggerFrameRadius = keyof typeof TRIGGER_FRAME_RADIUS;

const TRIGGER_FOCUS_CLASSES =
  'relative outline-none after:pointer-events-none after:absolute after:inset-0 after:border after:border-transparent after:content-[""] focus-visible:after:border-gray-900';

export function BottomSheet({
  open,
  onOpenChange,
  children,
  trigger,
  maxHeight = '85dvh',
  showCloseButton = true,
  className,
  showDragHandle = true,
  animation = 'slide',
  allowTriggerPointerEvents = false,
  triggerClassName,
  triggerFrameRadius = 'xl',
  onCloseAutoFocus,
  onClosed,
}: BottomSheetProps) {
  // mounted = keep in DOM; visible = trigger CSS transition
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  // Register with modal stack so nested overlays (e.g. a Modal opened from
  // inside this sheet) can pick a higher z-index. Use `mounted` so the sheet
  // keeps its assigned depth through the exit animation.
  const stackDepth = useModalStackDepth(mounted);
  const { backdrop: backdropZ, content: contentZ } = getStackZIndex(stackDepth);

  useEffect(() => {
    if (open) {
      // Mount first, then animate in on next frame
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      // Animate out, then unmount after transition ends
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const wasMountedRef = useRef(false);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;
  useEffect(() => {
    if (wasMountedRef.current && !mounted) onClosedRef.current?.();
    wasMountedRef.current = mounted;
  }, [mounted]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        // Animate out before telling parent to close
        setVisible(false);
        setTimeout(() => onOpenChange(false), ANIMATION_DURATION);
      } else {
        onOpenChange(true);
      }
    },
    [onOpenChange]
  );

  return (
    <DialogPrimitive.Root open={mounted} onOpenChange={handleOpenChange}>
      {trigger && (
        <DialogPrimitive.Trigger asChild>
          <div role="button" tabIndex={0} className={cn(TRIGGER_FOCUS_CLASSES, TRIGGER_FRAME_RADIUS[triggerFrameRadius], triggerClassName)}>
            <div className={allowTriggerPointerEvents ? '' : 'pointer-events-none'}>
              {trigger}
            </div>
          </div>
        </DialogPrimitive.Trigger>
      )}

      <DialogPrimitive.Portal forceMount={mounted ? undefined : undefined}>
        {mounted && (
          <>
            {/* Backdrop */}
            <DialogPrimitive.Overlay
              forceMount
              style={{ zIndex: backdropZ }}
              className={cn(
                'fixed inset-0 bg-black/50',
                'transition-opacity duration-300 ease-out',
                visible ? 'opacity-100' : 'opacity-0'
              )}
            />

            {/* Sheet content */}
            <DialogPrimitive.Content
              forceMount
              style={{ maxHeight, zIndex: contentZ }}
              className={cn(
                'fixed inset-x-0 bottom-0 rounded-t-2xl',
                'bg-white shadow-2xl pb-[env(safe-area-inset-bottom)]',
                'flex flex-col overflow-hidden',
                animation === 'slide'
                  ? 'transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]'
                  : 'transition-all duration-300 ease-out',
                animation === 'slide'
                  ? (visible ? 'translate-y-0' : 'translate-y-full')
                  : (visible ? 'opacity-100 scale-100' : 'opacity-0 scale-[0.97]'),
                className
              )}
              onEscapeKeyDown={(e) => {
                // Radix runs this in the capture phase, before an inner select/combobox can consume the key.
                // If the Esc belongs to an open inline popup, let that close first (same guard as Modal).
                if (shouldYieldEscape(e)) {
                  e.preventDefault();
                  return;
                }
                handleOpenChange(false);
              }}
              onCloseAutoFocus={onCloseAutoFocus}
            >
              {/* Accessible title and description (visually hidden) */}
              <VisuallyHidden.Root>
                <DialogPrimitive.Title>Bottom Sheet</DialogPrimitive.Title>
                <DialogPrimitive.Description>
                  A slide-up panel with additional content.
                </DialogPrimitive.Description>
              </VisuallyHidden.Root>

              {/* Drag-handle visual (decorative) */}
              {showDragHandle && (
                <div className="flex flex-shrink-0 justify-center pt-2.5 pb-1" aria-hidden="true">
                  <span className="h-1 w-10 rounded-full bg-gray-300" />
                </div>
              )}

              {/* Close button — absolute over content */}
              {showCloseButton && (
                <DialogPrimitive.Close
                  className={cn(
                    'absolute top-4 right-3 z-10',
                    'group flex h-[44px] w-[44px] items-center justify-center rounded-full',
                    'text-gray-700 transition-colors duration-150',
                    'focus:outline-none'
                  )}
                  aria-label="Close"
                >
                  <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm backdrop-blur-sm transition-colors group-hover:bg-gray-50 group-focus-visible:border-gray-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </span>
                </DialogPrimitive.Close>
              )}

              {/* Scrollable content area */}
              <div
                className="overflow-y-auto overscroll-contain flex-1 min-h-0"
                onClickCapture={(e) => {
                  const target = (e.target as HTMLElement).closest('[data-close-sheet]');
                  if (target) {
                    // Delay close so the button's onClick fires first
                    setTimeout(() => handleOpenChange(false), 100);
                  }
                }}
              >
                {children}
              </div>
            </DialogPrimitive.Content>
          </>
        )}
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
